import { useState, useEffect, useMemo } from 'react';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { DriverAnalysis, TabId, DailySummary, WeeklySummary, Infringement } from '../types';
import { PortalLayout } from '../components/PortalLayout';
import { OverviewTab } from './OverviewTab';
import { DailyTab } from './DailyTab';
import { WeeklyTab } from './WeeklyTab';
import { InfringementsTab } from './InfringementsTab';
import { VehiclesTab } from './VehiclesTab';
import { EventsTab } from './EventsTab';
import { PlacesTab } from './PlacesTab';
import { ChartTab } from './ChartTab';
import { FleetTab } from './FleetTab';
import { ExportTab } from './ExportTab';
import { WTDTab } from './WTDTab';

interface DriverListItem {
  id: number;
  card_number: string;
  surname: string;
  first_names: string;
  last_download?: string;
  assigned_manager_id?: number | null;
  assigned_manager_name?: string | null;
}

const TABS: { id: TabId; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'charts', label: 'Charts' },
  { id: 'daily', label: 'Daily' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'fleet', label: 'Fleet' },
  { id: 'infringements', label: 'Infringements' },
  { id: 'vehicles', label: 'Vehicles' },
  { id: 'events', label: 'Events' },
  { id: 'places', label: 'Places' },
  { id: 'wtd', label: 'WTD' },
  { id: 'export', label: 'Export' },
];

interface ManagerOption {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

export function TachoPage() {
  const { user: currentUser, isAdmin } = useAuth();
  const [analysis, setAnalysis] = useState<DriverAnalysis | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [apiOnline, setApiOnline] = useState(false);

  // Driver list
  const [drivers, setDrivers] = useState<DriverListItem[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<number | null>(null);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [driverSearch, setDriverSearch] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [infringementNotes, setInfringementNotes] = useState<any[]>([]);
  const [managers, setManagers] = useState<ManagerOption[]>([]);

  useEffect(() => {
    api.health().then(() => {
      setApiOnline(true);
      loadDrivers().then(() => {
        // Auto-select driver if ?driver=ID is in URL (e.g. from Fleet → Tacho link)
        const params = new URLSearchParams(window.location.search);
        const driverParam = params.get('driver');
        if (driverParam) {
          const driverId = parseInt(driverParam, 10);
          if (!isNaN(driverId)) {
            selectDriver(driverId);
          }
          // Clean up the URL so it doesn't re-trigger on refresh
          window.history.replaceState({}, '', window.location.pathname);
        }
      });
      // Load manager list for assignment dropdown (admin only)
      if (isAdmin) {
        api.getManagers().then(setManagers).catch(() => {});
      }
    }).catch(() => setApiOnline(false));
  }, []);

  const [driverLoadError, setDriverLoadError] = useState('');

  async function loadDrivers() {
    setLoadingDrivers(true);
    setDriverLoadError('');
    try {
      const data = await api.getDrivers({ page_size: 500 });
      setDrivers(Array.isArray(data) ? data : data.drivers || []);
    } catch (err: any) {
      console.error('Failed to load drivers:', err);
      setDriverLoadError(err?.message || 'Failed to load drivers');
    } finally {
      setLoadingDrivers(false);
    }
  }

  async function selectDriver(driverId: number) {
    setSelectedDriverId(driverId);
    setLoadingAnalysis(true);
    setAnalysis(null);
    setInfringementNotes([]);
    try {
      const data = await api.getDriverAnalysis(driverId);
      setAnalysis(data as DriverAnalysis);
      // Load infringement notes for badge count
      if (data?.driver?.id && data.driver.id > 0) {
        api.getInfringementNotes(data.driver.id).then(setInfringementNotes).catch(() => {});
      }
    } catch {
      setAnalysis(null);
    } finally {
      setLoadingAnalysis(false);
    }
  }

  // Date-filtered analysis
  const filteredAnalysis = useMemo(() => {
    if (!analysis) return null;
    if (!dateFrom && !dateTo) return analysis;

    const from = dateFrom || '0000-00-00';
    const to = dateTo || '9999-99-99';

    const daily = analysis.daily_breakdown?.filter(d => d.date >= from && d.date <= to) ?? [];
    const weekly = analysis.weekly_breakdown?.filter(w => {
      // Week format is "YYYY-Wnn" — use first daily date in that week range
      const weekDays = daily.filter(d => {
        const dt = new Date(d.date);
        const yr = dt.getFullYear();
        const wk = Math.ceil(((dt.getTime() - new Date(yr, 0, 1).getTime()) / 86400000 + new Date(yr, 0, 1).getDay() + 1) / 7);
        return w.week === `${yr}-W${String(wk).padStart(2, '0')}`;
      });
      return weekDays.length > 0;
    }) ?? analysis.weekly_breakdown;
    const infringements = analysis.infringements?.filter(inf => inf.date >= from && inf.date <= to) ?? [];

    // Recalculate summary from filtered daily data
    const totalDrivingMin = daily.reduce((s, d) => s + d.driving_minutes, 0);
    const totalWorkMin = daily.reduce((s, d) => s + d.work_minutes, 0);
    const totalRestMin = daily.reduce((s, d) => s + d.rest_minutes, 0);
    const totalAvailMin = daily.reduce((s, d) => s + d.availability_minutes, 0);
    const totalDist = daily.reduce((s, d) => s + d.distance_km, 0);
    const daysWorked = daily.filter(d => d.driving_minutes > 0 || d.work_minutes > 0).length;

    return {
      ...analysis,
      daily_breakdown: daily,
      weekly_breakdown: weekly,
      infringements,
      summary: {
        ...analysis.summary,
        total_driving_hours: +(totalDrivingMin / 60).toFixed(1),
        total_driving_minutes: totalDrivingMin,
        total_work_hours: +(totalWorkMin / 60).toFixed(1),
        total_rest_hours: +(totalRestMin / 60).toFixed(1),
        total_availability_hours: +(totalAvailMin / 60).toFixed(1),
        total_distance_km: +totalDist.toFixed(1),
        days_worked: daysWorked,
        avg_daily_driving_hours: daysWorked > 0 ? +(totalDrivingMin / 60 / daysWorked).toFixed(1) : 0,
        avg_daily_distance_km: daysWorked > 0 ? +(totalDist / daysWorked).toFixed(1) : 0,
      },
    } as DriverAnalysis;
  }, [analysis, dateFrom, dateTo]);

  // Count only unresolved infringements (those without notes) for the badge
  const totalInfringements = filteredAnalysis?.infringements?.length ?? 0;
  const notedInfringementKeys = new Set(
    infringementNotes.map(n => `${n.infringement_date}::${n.infringement_type}`)
  );
  const unresolvedInfringementCount = (filteredAnalysis?.infringements ?? []).filter(
    inf => !notedInfringementKeys.has(`${inf.date}::${inf.type}`)
  ).length;
  const infringementCount = unresolvedInfringementCount;
  const driver = filteredAnalysis?.driver;

  const filteredDrivers = drivers.filter(d => {
    if (!driverSearch) return true;
    const s = driverSearch.toLowerCase();
    return d.surname.toLowerCase().includes(s) ||
           d.first_names.toLowerCase().includes(s) ||
           d.card_number.toLowerCase().includes(s);
  });

  return (
    <PortalLayout currentPage="tacho">
      <div className="flex h-[calc(100vh-0px)]">
        {/* Driver sidebar */}
        <div className="w-72 bg-white border-r border-gray-200 flex flex-col flex-shrink-0">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">Drivers</h2>
            <input
              type="text"
              placeholder="Search drivers..."
              value={driverSearch}
              onChange={(e) => setDriverSearch(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-400 focus:border-transparent"
            />
          </div>

          <div className="flex-1 overflow-y-auto">
            {loadingDrivers ? (
              <div className="p-4 text-center text-gray-400 text-sm">Loading...</div>
            ) : driverLoadError ? (
              <div className="p-4 text-center text-sm">
                <p className="text-red-500 mb-2">Error loading drivers</p>
                <p className="text-red-400 text-xs mb-2">{driverLoadError}</p>
                <button onClick={loadDrivers} className="text-blue-600 underline text-xs hover:text-blue-700">Retry</button>
              </div>
            ) : filteredDrivers.length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-sm">
                {drivers.length === 0 ? (
                  <>
                    <p className="mb-1">No drivers yet</p>
                    <p className="text-xs">Upload a tachograph card file or use the card reader to add drivers</p>
                  </>
                ) : (
                  <p>No matching drivers</p>
                )}
              </div>
            ) : (
              filteredDrivers.map((d) => (
                <button
                  key={d.id}
                  onClick={() => selectDriver(d.id)}
                  className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    selectedDriverId === d.id ? 'bg-blue-50 border-l-2 border-l-blue-600' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 bg-slate-100 rounded flex items-center justify-center text-[11px] font-semibold text-slate-600 flex-shrink-0">
                      {d.first_names?.[0]}{d.surname?.[0]}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {d.first_names} {d.surname}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{d.card_number}</p>
                      {d.last_download && (
                        <p className="text-xs text-gray-400">
                          Last: {new Date(d.last_download).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} {new Date(d.last_download).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                      {isAdmin && d.assigned_manager_name && (
                        <p className="text-xs text-blue-600 truncate">TM: {d.assigned_manager_name}</p>
                      )}
                      {isAdmin && !d.assigned_manager_id && (
                        <p className="text-xs text-amber-500">No TM assigned</p>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Bottom actions */}
          <div className="p-3 border-t border-gray-200 space-y-2">
            <button
              onClick={loadDrivers}
              className="w-full text-xs px-3 py-2 bg-gray-50 text-gray-500 rounded hover:bg-gray-100 transition-colors"
            >
              Refresh List
            </button>
            {statusMsg && (
              <p className="text-xs text-center text-blue-600">{statusMsg}</p>
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden min-w-0">
          <div className="p-5">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-sm font-semibold uppercase tracking-wide text-gray-900">Tachograph Analysis</h1>
                <p className="text-gray-500 text-sm mt-1">
                  {drivers.length} driver{drivers.length !== 1 ? 's' : ''} · Select from the list to view analysis
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full ${apiOnline ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-xs text-gray-400">{apiOnline ? 'Connected' : 'Offline'}</span>
              </div>
            </div>

            {/* Loading */}
            {loadingAnalysis && (
              <div className="text-center py-16">
                <p className="text-gray-400 text-sm">Loading analysis...</p>
              </div>
            )}

            {/* Analysis view */}
            {!loadingAnalysis && filteredAnalysis ? (
              <>
                {/* Driver info strip */}
                <div className="bg-white rounded border border-gray-200 mb-4">
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                    <div className="flex items-center gap-6">
                      <h2 className="text-sm font-semibold text-gray-900">
                        {driver?.first_names} {driver?.surname}
                      </h2>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>Card: <span className="font-medium text-gray-700">{driver?.card_number}</span></span>
                        {driver?.licence_number && <span>Licence: <span className="font-medium text-gray-700">{driver.licence_number}</span></span>}
                        {driver?.card_expiry && <span>Expires: <span className="font-medium text-gray-700">{new Date(driver.card_expiry).toLocaleDateString('en-GB')}</span></span>}
                        <span>Gen <span className="font-medium text-gray-700">{driver?.card_generation ?? '?'}</span></span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {infringementCount > 0 && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">
                          {infringementCount} infringement{infringementCount !== 1 ? 's' : ''}
                        </span>
                      )}
                      {infringementCount === 0 && (
                        <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs font-medium">No infringements</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between px-4 py-1.5 flex-wrap gap-2">
                    {driver?.last_download && (
                      <p className="text-xs text-gray-400">
                        Last download: {new Date(driver.last_download).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} {new Date(driver.last_download).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}
                    {isAdmin && selectedDriverId && (
                      <div className="flex items-center gap-2">
                        <label className="text-xs font-medium text-gray-500">Assigned TM:</label>
                        <select
                          value={
                            drivers.find(d => d.id === selectedDriverId)?.assigned_manager_id ?? ''
                          }
                          onChange={async (e) => {
                            const managerId = e.target.value ? parseInt(e.target.value) : null;
                            try {
                              await api.assignManager(selectedDriverId, managerId);
                              // Update local state
                              setDrivers(prev => prev.map(d =>
                                d.id === selectedDriverId
                                  ? {
                                      ...d,
                                      assigned_manager_id: managerId,
                                      assigned_manager_name: managerId
                                        ? managers.find(m => m.id === managerId)
                                          ? `${managers.find(m => m.id === managerId)!.first_name} ${managers.find(m => m.id === managerId)!.last_name}`
                                          : null
                                        : null,
                                    }
                                  : d
                              ));
                              setStatusMsg(managerId ? 'Manager assigned' : 'Manager unassigned');
                              setTimeout(() => setStatusMsg(''), 2000);
                            } catch (err: any) {
                              setStatusMsg(`Error: ${err.message}`);
                            }
                          }}
                          className="text-xs border border-gray-200 rounded px-2 py-1.5 bg-white focus:ring-2 focus:ring-blue-400 min-w-[180px]"
                        >
                          <option value="">— Unassigned —</option>
                          {managers.map(m => (
                            <option key={m.id} value={m.id}>{m.first_name} {m.last_name} ({m.role === 'admin' ? 'Admin' : 'TM'})</option>
                          ))}
                        </select>
                      </div>
                    )}
                    {!isAdmin && selectedDriverId && (() => {
                      const d = drivers.find(d => d.id === selectedDriverId);
                      return d?.assigned_manager_name ? (
                        <p className="text-xs text-gray-400">
                          Managed by: <span className="font-medium text-gray-600">{d.assigned_manager_name}</span>
                        </p>
                      ) : null;
                    })()}
                  </div>
                </div>

                {/* Date period filter */}
                <div className="bg-white rounded border border-gray-200 p-3 mb-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-sm font-medium text-gray-700">Period:</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                      />
                      <span className="text-gray-400 text-sm">to</span>
                      <input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="px-3 py-1.5 text-sm border border-gray-200 rounded focus:ring-2 focus:ring-blue-400 focus:border-transparent"
                      />
                    </div>
                    {(dateFrom || dateTo) && (
                      <button
                        onClick={() => { setDateFrom(''); setDateTo(''); }}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Clear filter
                      </button>
                    )}
                    {(dateFrom || dateTo) && filteredAnalysis && (
                      <span className="text-xs text-gray-400">
                        Showing {filteredAnalysis.daily_breakdown?.length ?? 0} of {analysis?.daily_breakdown?.length ?? 0} days
                      </span>
                    )}
                  </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200 mb-4">
                  <nav className="flex flex-wrap gap-4">
                    {TABS.map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`px-0 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'text-blue-700 border-b-blue-600'
                            : 'text-gray-500 border-b-transparent hover:text-gray-700'
                        }`}
                      >
                        {tab.label}
                        {tab.id === 'infringements' && infringementCount > 0 && (
                          <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded">
                            {infringementCount}
                          </span>
                        )}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab content */}
                <div className="bg-white rounded border border-gray-200 p-5">
                  {activeTab === 'overview' && <OverviewTab analysis={filteredAnalysis} infringementNotes={infringementNotes} />}
                  {activeTab === 'charts' && <ChartTab analysis={filteredAnalysis} />}
                  {activeTab === 'daily' && <DailyTab analysis={filteredAnalysis} />}
                  {activeTab === 'weekly' && <WeeklyTab analysis={filteredAnalysis} />}
                  {activeTab === 'fleet' && <FleetTab analysis={filteredAnalysis} />}
                  {activeTab === 'infringements' && <InfringementsTab analysis={filteredAnalysis} onNotesChanged={setInfringementNotes} />}
                  {activeTab === 'vehicles' && <VehiclesTab analysis={filteredAnalysis} />}
                  {activeTab === 'events' && <EventsTab analysis={filteredAnalysis} />}
                  {activeTab === 'places' && <PlacesTab analysis={filteredAnalysis} />}
                  {activeTab === 'wtd' && <WTDTab driverId={selectedDriverId} />}
                  {activeTab === 'export' && <ExportTab analysis={filteredAnalysis} driverId={selectedDriverId ?? undefined} />}
                </div>
              </>
            ) : !loadingAnalysis ? (
              /* Empty state */
              <div className="text-center py-16">
                <h2 className="text-sm font-semibold text-gray-700 mb-2">
                  {selectedDriverId ? 'No Analysis Available' : 'Select a Driver'}
                </h2>
                <p className="text-gray-400 text-sm max-w-md mx-auto">
                  {selectedDriverId
                    ? 'No card data has been uploaded for this driver yet.'
                    : drivers.length > 0
                    ? 'Choose a driver from the list on the left.'
                    : 'No drivers in the system yet. Upload a tachograph card file (.ddd) or use the card reader to add drivers.'
                  }
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </PortalLayout>
  );
}
